/**
 * Shared adapters between the backend Ticket shape and the various
 * UI row shapes the call-center pages render. Keeps the per-page
 * Types files lean — they just import what they need from here.
 */
import type {
  Ticket,
  TicketPriority as BackendPriority,
  TicketStatus as BackendStatus,
  TicketCategory,
} from '../../../services/ticketsApi';
import type { Priority, TicketStatus } from './';
import { formatOrderId } from '../../../utils/formatId';

const PRIORITY_LABEL: Record<BackendPriority, Priority> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_TO_BACKEND: Record<Priority, BackendPriority> = {
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

const STATUS_LABEL: Record<BackendStatus, TicketStatus> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_TO_BACKEND: Record<TicketStatus, BackendStatus> = {
  Open: 'open',
  'In Progress': 'in_progress',
  Waiting: 'waiting',
  Resolved: 'resolved',
  Closed: 'closed',
  'Pending Review': 'pending_review',
  Approved: 'approved',
  Rejected: 'rejected',
};

export function uiPriority(p: BackendPriority): Priority {
  return PRIORITY_LABEL[p] ?? 'Medium';
}

export function backendPriority(p: Priority): BackendPriority {
  return PRIORITY_TO_BACKEND[p] ?? 'medium';
}

export function uiStatus(s: BackendStatus): TicketStatus {
  return STATUS_LABEL[s] ?? 'Open';
}

export function backendStatus(s: TicketStatus): BackendStatus {
  return STATUS_TO_BACKEND[s] ?? 'open';
}

export function categoryToModule(c: TicketCategory): string {
  switch (c) {
    case 'customer': return 'Customer';
    case 'delivery': return 'Delivery';
    case 'payment': return 'Payment';
    case 'inventory': return 'Inventory';
    case 'compliance': return 'Compliance';
    case 'returns': return 'Return';
    default: return 'Other';
  }
}

export function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * Generic ticket adapter for the issue-category tables. Each page
 * picks the fields it needs from the resulting Ticket via this shape.
 */
export interface IssueRow {
  id: string;
  ticketId: string;
  customer: string;
  orderId: string;
  issueType: string;
  priority: Priority;
  status: TicketStatus;
  assignedTo: string;
}

export function adaptIssueRow(t: Ticket): IssueRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    customer: t.customer_name || '—',
    orderId: t.order_id ? formatOrderId(t.order_id) : '—',
    issueType: t.subject,
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
    assignedTo: t.assigned_to_name || 'Unassigned',
  };
}
